import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const Footer = () => {
  const { t } = useTranslation();

  const currentYear = new Date().getFullYear();
  const copyrightYears = `2025–${currentYear}`;

  return (
    <footer className="bg-dark text-white pt-12 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-1">
            <div className="mb-4">
              <Logo size="medium" color="white" textColor="white" />
            </div>
            <p className="text-gray-300 mb-4">
              {t('about.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/rokwansoft/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white" aria-label="Facebook">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.675 0h-21.35C.597 0 0 .597 0 1.326v21.348C0 23.403.597 24 1.326 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.403 24 24 23.403 24 22.674V1.326C24 .597 23.403 0 22.675 0z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/rokwansoft/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white" aria-label="Instagram">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 7.2a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6Zm0 7.9a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2Zm6.1-8.2a1.12 1.12 0 1 1-2.24 0 1.12 1.12 0 0 1 2.24 0ZM7.2 2h9.6A5.2 5.2 0 0 1 22 7.2v9.6A5.2 5.2 0 0 1 16.8 22H7.2A5.2 5.2 0 0 1 2 16.8V7.2A5.2 5.2 0 0 1 7.2 2Zm0 1.8A3.4 3.4 0 0 0 3.8 7.2v9.6A3.4 3.4 0 0 0 7.2 20.2h9.6a3.4 3.4 0 0 0 3.4-3.4V7.2a3.4 3.4 0 0 0-3.4-3.4H7.2Z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1">
            <h3 className="text-xl font-bold mb-4">{t('header.home')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.about')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.products')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.pricing')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.blog')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-secondary transition-colors">
                  {t('header.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="col-span-1">
            <h3 className="text-xl font-bold mb-4">{t('home.services.title')}</h3>
            <ul className="space-y-2">
              <li className="text-gray-300 hover:text-secondary transition-colors">{t('home.services.webDev')}</li>
              <li className="text-gray-300 hover:text-secondary transition-colors">{t('home.services.mobileDev')}</li>
              <li className="text-gray-300 hover:text-secondary transition-colors">{t('home.services.cloudSolutions')}</li>
              <li className="text-gray-300 hover:text-secondary transition-colors">{t('home.services.consultancy')}</li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-1">
            <h3 className="text-xl font-bold mb-4">{t('header.contact')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-3">
                <svg className="h-6 w-6 text-gray-300 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h2l3 7-1.5 1.5a11 11 0 005 5L13 17l7 3v2a2 2 0 01-2 2A18 18 0 013 5z" />
                </svg>
                <a href="tel:+527294441897" className="text-gray-300 hover:text-white">
                  {t('footer.phone')}: {t('footer.phoneValue')}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <svg className="h-6 w-6 text-gray-300 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:contact@rokwansoft.com" className="text-gray-300 hover:text-white">contact@rokwansoft.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-700 text-center text-gray-400">
          <p>&copy; {copyrightYears} Rokwansoft. {t('footer.copyright')}</p>
          <p className="mt-2">{t('footer.brandNote')}</p>
          <div className="mt-3 flex flex-col items-center space-y-2">
            <a href="https://www.rokwansoft.com" className="hover:text-white">www.rokwansoft.com</a>
            <div className="flex space-x-4 text-sm">
              <Link to="/terms" className="text-gray-300 hover:text-white">{t('legal.links.terms')}</Link>
              <span className="text-gray-500">|</span>
              <Link to="/privacy" className="text-gray-300 hover:text-white">{t('legal.links.privacy')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
